import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { RiDeleteBin5Line } from 'react-icons/ri';
import { FaEye } from 'react-icons/fa6';
import { BeatLoader } from 'react-spinners';
import Pagination from '../Pagination';
import Title from '../Title';
import { formatYear } from '../../utils/Date';
import {
  deleteBusiness,
  deactivateBusiness,
  fetchBusinesses,
  viewBusiness,
  reactivateBusiness,
} from '../../services/BusinessManagement/BusinessListService';
import { showConfirmToast, showSuccess, showError } from '../../components/Ui/ToastNotification';
import { CiPower } from 'react-icons/ci';

const BusinessList = () => {
  const navigate = useNavigate();
  const [activePopoverId, setActivePopoverId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentPage, entriesPerPage } = useSelector(state => state.pagination);
  const searchQuery = useSelector(state => state.search.query);

  useEffect(() => {
    let isMounted = true;

    const loadBusinesses = async () => {
      setLoading(true);
      try {
        const businesses = await fetchBusinesses(filterStatus);
        if (isMounted) {
          setPeople(businesses);
          showSuccess('Businesses loaded successfully');
        }
      } catch (error) {
        console.error('Error loading businesses:', error);
        showError('Failed to load businesses');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadBusinesses();

    return () => {
      isMounted = false;
    };
  }, [filterStatus]);

  const handleDelete = async businessId => {
    showConfirmToast({
      message:
        'Are you sure you want to permanently delete this business? This action cannot be undone.',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await deleteBusiness(businessId);
          setPeople(prev => prev.filter(business => business._id !== businessId));
          showSuccess('Business deleted successfully');
        } catch (error) {
          showError(error.response?.data?.message || 'Failed to delete business');
        }
      },
    });
  };

  const handleStatusToggle = async (businessId, currentStatus) => {
    const isActivating = currentStatus === 'disable' || !currentStatus || currentStatus === '';
    const action = isActivating ? 'activate' : 'deactivate';

    showConfirmToast({
      message: `Are you sure you want to ${action} this business?`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      onConfirm: async () => {
        try {
          isActivating
            ? await reactivateBusiness(businessId)
            : await deactivateBusiness(businessId);

          setPeople(prev =>
            prev.map(business =>
              business._id === businessId
                ? {
                    ...business,
                    status: isActivating ? 'active' : 'disable',
                  }
                : business
            )
          );

          showSuccess(`Business ${action}d successfully`);
        } catch (error) {
          showError(error.response?.data?.message || `Failed to ${action} business`);
        }
      },
    });
  };

  const filteredPeople = people.filter(user => {
    return searchQuery
      ? [user?.name, user?.legalEntityType].some(field =>
          field?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : true;
  });

  const statusConfig = {
    disable: { color: 'text-green-600', text: 'Reactivate' },
    pending: { color: 'text-green-600', text: 'Activate' },
    active: { color: 'text-orange-600', text: 'Deactivate' },
    default: { color: 'text-green-600', text: 'Activate' },
  };

  const paginatedOrganizations = filteredPeople.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const togglePopover = id => {
    setActivePopoverId(prev => (prev === id ? null : id));
  };

  const EmptyBusinessState = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-lg">
      <div className="mb-6 text-gray-400">
        <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      </div>
      <h3 className="mb-3 text-lg font-light text-gray-600">
        {searchQuery ? 'No matching businesses found' : 'No businesses available'}
      </h3>
      <p className="max-w-md mb-6 text-sm font-light text-gray-500">
        {searchQuery
          ? 'No businesses match your search criteria. Try adjusting your search terms or filter settings.'
          : 'There are no businesses in the system yet. Businesses will appear here once they are registered and approved.'}
      </p>
      {!searchQuery && (
        <div className="space-y-2">
          <p className="text-xs font-light text-gray-400">
            Check back later or adjust your filter settings
          </p>
        </div>
      )}
    </div>
  );

  const renderBusinessRow = org => {
    const displayStatus = org?.status || 'pending';

    const statusConf = statusConfig[org?.status] || statusConfig.default;

    return (
      <tr
        key={org._id}
        onClick={e => {
          viewBusiness(navigate, org?._id);
        }}
        className="cursor-pointer text-[#232323] text-sm font-normal even:bg-[#f9f9f9] odd:bg-[#fff]">
        <td className="px-2 py-2 w-[50px]">
          <img
            className="w-[30px] h-[30px] rounded-full"
            src={org?.imageUrl || org?.media?.[0]?.fileUrl || '/default-image.png'}
            alt={org?.name}
          />
        </td>
        <td className="p-2">{org?.name || '-'}</td>
        <td className="p-2">
          {org?.location?.address || org?.locations || org?.location || 'Location not specified'}
        </td>
        <td className="p-2">{org?.legalEntityType || '-'}</td>
        <td
          className={`p-2 ${
            displayStatus === 'active'
              ? 'text-[#0CB226]'
              : displayStatus === 'disable'
              ? 'text-red-500'
              : 'text-[#E1A906]'
          }`}>
          {displayStatus}
        </td>
        <td className="px-6 py-4">{formatYear(org?.yearEstablished) || '-'}</td>
        <td className="relative w-8 h-8">
          <button
            className="focus:ring-0 focus:outline-none mx-2 w-[80%] h-[50%] rounded-full"
            onClick={e => {
              e.stopPropagation();
              togglePopover(org?._id);
            }}>
            <BsThreeDotsVertical className="text-[#191B1C]" />
          </button>

          {activePopoverId === org._id && (
            <div className="absolute z-10 mt-1 text-sm text-gray-500 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-md top-full left-1/2 w-max">
              <div className="flex flex-col p-2 space-y-1">
                <button
                  onClick={e => {
                    e.stopPropagation();
                    viewBusiness(navigate, org?._id);
                  }}
                  className="inline-flex items-center text-[10px] text-gray-700">
                  <FaEye className="w-4 h-4 m-1" /> View
                </button>

                <button
                  onClick={e => {
                    e.stopPropagation();
                    handleDelete(org?._id);
                  }}
                  className="inline-flex items-center text-[10px] text-red-600">
                  <RiDeleteBin5Line className="w-4 h-4 m-1" /> Delete
                </button>

                <button
                  onClick={e => {
                    e.stopPropagation();
                    handleStatusToggle(org?._id, org?.status);
                  }}
                  className={`inline-flex items-center text-[10px] ${statusConf.color}`}>
                  <CiPower className="w-4 h-4 m-1" />
                  {statusConf.text}
                </button>
              </div>
            </div>
          )}
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div
        data-testid="loading-spinner"
        className="p-4 sm:ml-64 w-[80%] h-screen bg-[#F4F5F6] flex justify-center items-center">
        <BeatLoader color="#000000" size={15} />
      </div>
    );
  }

  return (
    <div data-testid="business-profile" className="p-4 sm:ml-64 w-[80%] bg-[#F4F5F6] font-nohemi">
      <div className="">
        <div className="flex items-center mb-6">
          <Title title="Business Listing" subtitle="Business Listing &gt; Companies/Business" />
        </div>
        <div className="relative overflow-x-auto bg-[#f8f9fa] rounded-lg">
          <div className="flex mx-6 my-2 space-x-1">
            {['all', 'approved', 'pending'].map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-6 py-2 text-[#404D61] text-sm rounded-2xl ${
                  filterStatus === status
                    ? 'border border-black'
                    : 'hover:border hover:border-black'
                }`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          {filteredPeople.length === 0 ? (
            <EmptyBusinessState />
          ) : (
            <div className="w-full p-6 bg-white rounded-t-2xl">
              <div className="w-full pt-2 bg-[#F7F7F7] rounded-t-2xl">
                <table className="w-full text-left rtl:text-right">
                  <thead>
                    <tr className="text-sm font-semibold text-[#444444] bg-[#F7F7F7]">
                      <td className="p-3" />
                      <td className="p-3">Organization Name</td>
                      <td className="p-3">Location</td>
                      <td className="p-3">Business Type</td>
                      <td className="p-3">Status</td>
                      <td className="p-3">Year of Est.</td>
                      <td className="p-3">Action</td>
                    </tr>
                  </thead>
                  <tbody>{paginatedOrganizations.map(renderBusinessRow)}</tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        {filteredPeople.length > entriesPerPage && (
          <Pagination totalEntries={filteredPeople.length} />
        )}
      </div>
    </div>
  );
};

export default BusinessList;
